import type { GlyphKind } from '@common/components/kind-glyphs/send-kind';
import type { SlotPlan } from './destination-plan';

/**
 * The avatar with two corner badges — the classic pattern the owner asked for:
 *
 *   top end corner      the count ("+5"), only when there is more than one
 *   bottom end corner   the kind (message / campaign / Status), always
 *
 * Same size, same end edge, mirrored top and bottom, so they balance each
 * other; the face in between stays whole. "End" is logical: the right edge in
 * English, the left in Hebrew.
 *
 * Pure. The numbers below are the one source for the stylesheet (read back as
 * custom properties) and for the tests that measure how much of the face is
 * left uncovered.
 */

export interface SlotGeometry {
    /** The slot every row reserves — titles line up against its end edge. */
    slotW: number;
    slotH: number;
    /** Face diameter. */
    face: number;
    /** Badge diameter (a count wider than "+99" grows into a pill towards the face). */
    badge: number;
    /** The surface-coloured ring that separates a badge from the face. */
    ring: number;
    /** Count text size. */
    text: number;
    /** Kind-badge glyph size. */
    glyph: number;
}

export const SLOT_GEOMETRY = {
    /**
     * A sidebar row. 44 wide is the slot established last round (titles start
     * at the same x on every row). Each badge's ring touches the slot's end
     * edge and its top (or bottom) edge, which puts its centre just outside the
     * face's rim — the badge rests ON the rim instead of over the face: with
     * both badges ~89% of the face stays visible, where the stacked "+N" left
     * 69%. The count badge is smaller than before (18 vs 22) and its text
     * larger (11 vs 9.5), as asked. Sized by measurement — see the geometry
     * tests; 56 tall fits the 76px a row leaves inside its padding. The kind
     * glyph is 14 so it reads the same size as the "+N" above it (an 11px MUI
     * glyph draws only ~9px of ink and looked a size smaller).
     */
    row: { slotW: 44, slotH: 56, face: 36, badge: 18, ring: 1.5, text: 11, glyph: 14 },
    /** A header line ("6 contacts"): a small face and its count, no kind badge,
     *  no taller than the line it sits in. */
    inline: { slotW: 38, slotH: 24, face: 24, badge: 16, ring: 1.5, text: 9.5, glyph: 9 },
} satisfies Record<string, SlotGeometry>;

export interface Point { x: number; y: number }

/**
 * Centres, measured from the slot's inline-START and block-start edges. The
 * face is at the inline start, vertically centred; each badge is placed so its
 * ring touches the slot's end edge and its top (or bottom) edge.
 */
export function badgeCentres(g: SlotGeometry): { faceX: number; faceY: number; topEnd: Point; bottomEnd: Point } {
    const r = g.badge / 2 + g.ring;
    const x = g.slotW - r;
    return {
        faceX: g.face / 2,
        faceY: g.slotH / 2,
        topEnd: { x, y: r },
        bottomEnd: { x, y: g.slotH - r },
    };
}

/** Fraction of the face NOT covered by these badges (ring included), sampled on a 0.1px grid. */
export function faceUncovered(g: SlotGeometry, corners: ReadonlyArray<'topEnd' | 'bottomEnd'>): number {
    const c = badgeCentres(g);
    const R = g.face / 2;
    const rb = g.badge / 2 + g.ring;
    const discs = corners.map(k => c[k]);
    let total = 0, visible = 0;
    for (let x = c.faceX - R; x <= c.faceX + R; x += 0.1) {
        for (let y = c.faceY - R; y <= c.faceY + R; y += 0.1) {
            if ((x - c.faceX) ** 2 + (y - c.faceY) ** 2 > R * R) continue;
            total++;
            if (!discs.some(p => (x - p.x) ** 2 + (y - p.y) ** 2 <= rb * rb)) visible++;
        }
    }
    return total ? visible / total : 1;
}

export interface CornerBadges {
    topEnd: { more: number } | null;
    bottomEnd: { kind: GlyphKind } | null;
}

/**
 * Which corner shows what. The count appears only when there is somebody
 * besides the face; the kind is always shown where the surface wants one
 * (`kind` null — a header line that already says what it is — wants none).
 */
export function planCornerBadges(slot: SlotPlan, kind: GlyphKind | null): CornerBadges {
    return {
        topEnd: !slot.isStatus && slot.more > 0 ? { more: slot.more } : null,
        bottomEnd: kind ? { kind } : null,
    };
}
